import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { FullWidthDropdown } from "component/shared/full-width-dropdown";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import { fetchMobileSettings, saveMobileSettings } from "redux/slice/mobile-settings";
import { MobileSettingsResource } from "api/resource/mobile-settings";
import { coldFusionReplatform } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const HEADER_COLOR_OPTIONS = [
	{ value: "grey", label: "Grey" },
	{ value: "green", label: "Green" },
	{ value: "red", label: "Red" },
	{ value: "gold", label: "Gold" },
	{ value: "blue", label: "Blue" },
];

const CUSTOM_HEADER_OPTIONS = [
	{ value: "1", label: "On homepage only" },
	{ value: "2", label: "On all IDX pages" },
	{ value: "0", label: "Off" },
];

const title = "Mobile Settings";

const REDIRECT_SCRIPT_TEMPLATE = (subdomain: string, clientId: number) =>
	`<script type="text/javascript">
	(function(a,b){if(!(navigator.platform =="ipad") && /android|avantgo|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\\/|plucker|pocket|psp|symbian|treo|up\\.(browser|link)|vodafone|wap|windows (ce|phone)|xda|xiino/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\\-(n|u)|c55\\/|capi|ccwa|cdm\\-|cell|chtm|cldc|cmd\\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\\-s|devi|dica|dmob|do(c|p)o|ds(12|\\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\\-|_)|g1 u|g560|gene|gf\\-5|g\\-mo|go(\\.w|od)|gr(ad|un)|haie|hcit|hd\\-(m|p|t)|hei\\-|hi(pt|ta)|hp( i|ip)|hs\\-c|ht(c(\\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\\-(20|go|ma)|i230|iac( |\\-|\\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\\/)|klon|kpt |kwc\\-|kyo(c|k)|le(no|xi)|lg( g|\\/(k|l|u)|50|54|e\\-|e\\/|\\-[a-w])|libw|lynx|m1\\-w|m3ga|m50\\/|ma(te|ui|xo)|mc(01|21|ca)|m\\-cr|me(di|rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\\-2|po(ck|rt|se)|prox|psio|pt\\-g|qa\\-a|qc(07|12|21|32|60|\\-[2-7]|i\\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\\-|oo|p\\-)|sdk\\/|se(c(\\-|0|1)|47|mc|nd|ri)|sgh\\-|shar|sie(\\-|m)|sk\\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\\-|v\\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\\-|tdg\\-|tel(i|m)|tim\\-|t\\-mo|to(pl|sh)|ts(70|m\\-|m3|m5)|tx\\-9|up(\\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|xda(\\-|2|g)|yas\\-|your|zeto|zte\\-/i.test(a.substr(0,4)))window.location=b})(navigator.userAgent||navigator.vendor||window.opera,'http://${subdomain}.idxre.com/idx/mobile/index.cfm?cid=${clientId}');
</script>`;

// CF token helper (mobileSettings1.cfm GetToken): 1-based, splits on the delimiter
// and — like CF lists — ignores empty elements from consecutive delimiters.
function getToken(value: string, index: number, delimiter: string): string {
	const tokens = value.split(delimiter).filter((token) => token.length > 0);
	return tokens[index - 1] || "";
}

// Port of CF's subdomain derivation (mobileSettings1.cfm:37-41) quirk-for-quirk:
//   subdomain = GetToken(GetToken(BaseURL, "2", "."), "1", ".")  -> 2nd dot-token of the RAW BaseURL
//   lower-cased; falls back to "www" when <=2 chars, exactly "com"/"net", or containing "/".
function extractSubdomain(baseUrl: string): string {
	const subdomain = getToken(getToken(baseUrl || "", 2, "."), 1, ".").toLowerCase();
	if (subdomain.length <= 2 || subdomain === "com" || subdomain === "net" || subdomain.includes("/")) {
		return "www";
	}
	return subdomain;
}

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && user.permissions.mobileSite}
		title={title}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.Settings />
				</FeatherIcon>
				{title}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/mobileSettings.cfm" />}
	/>
);

const NativePageBase: React.FC<Props> = ({ user, classes = {} }) => {
	const dispatch = useDispatch();
	const saved = useSelector((state: RootState) => state.mobileSettings.mobileSettings);
	const loading = useSelector((state: RootState) => state.mobileSettings.loading);
	const [form, setForm] = useState<MobileSettingsResource>({});
	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

	useEffect(() => {
		dispatch(fetchMobileSettings() as any);
	}, [dispatch]);

	useEffect(() => {
		if (saved) {
			setForm(saved);
			setDirty(false);
		}
	}, [saved]);

	const update = (patch: Partial<MobileSettingsResource>) => {
		setForm((prev) => ({ ...prev, ...patch }));
		setDirty(true);
	};

	const redirectCode = useMemo(() => {
		const subdomain = extractSubdomain(user.baseUrl);
		return REDIRECT_SCRIPT_TEMPLATE(subdomain, user.clientId);
	}, [user.baseUrl, user.clientId]);

	const handleSave = async () => {
		setSaving(true);
		try {
			await dispatch(saveMobileSettings(form) as any);
			setSnackbarMessage("Mobile settings saved");
			setSnackbarSeverity("success");
			setSnackbarOpen(true);
			setDirty(false);
		} catch{
			setSnackbarMessage("Failed to save mobile settings");
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		if (saved) {
			setForm(saved);
		}
		setDirty(false);
	};

	return (
		<DashboardLayout
			permitted={user && user.permissions.mobileSite}
			title={title}
			header={<Mui.Typography variant="h1">{title}</Mui.Typography>}
		>
			{loading && <Mui.LinearProgress />}

			{form.mobileRestricted && (
				<Mui.Box mb={2}>
					<MuiAlert severity="warning">
						One or more of your Boards have rules restricting the use of Mobile IDX.{" "}
						<Mui.Link href="http://www.ihomefinder.com/mls-coverage/mobile-idx-policies/" target="_blank" rel="noopener noreferrer">
							View Mobile IDX policies
						</Mui.Link>.
					</MuiAlert>
				</Mui.Box>
			)}

			<FullPageCard>
				<Mui.Grid container direction="column" spacing={3}>
					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Mobile Site</Mui.Typography>
						<Mui.RadioGroup
							value={form.mobileSiteYn ? "1" : "0"}
							onChange={(e) => update({ mobileSiteYn: e.target.value === "1" })}
							row
						>
							<Mui.FormControlLabel value="1" control={<Mui.Radio color="secondary" />} label="On" />
							<Mui.FormControlLabel value="0" control={<Mui.Radio color="secondary" />} label="Off" />
						</Mui.RadioGroup>
					</Mui.Grid>

					<Mui.Grid item>
						<FullWidthDropdown<string>
							label="Mobile Header Background Color"
							value={form.mobileHeaderBgColor || "grey"}
							onChange={(value) => update({ mobileHeaderBgColor: value })}
							maxWidth={576}
						>
							{HEADER_COLOR_OPTIONS.map((opt) => (
								<Mui.MenuItem key={opt.value} value={opt.value}>{opt.label}</Mui.MenuItem>
							))}
						</FullWidthDropdown>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Header Text Line 1</Mui.Typography>
						<Mui.TextField
							fullWidth
							variant="outlined"
							size="small"
							inputProps={{ maxLength: 50 }}
							value={form.mobileHeaderText1 || ""}
							onChange={(e) => update({ mobileHeaderText1: e.target.value })}
							style={{ maxWidth: 576 }}
						/>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Header Text Line 2</Mui.Typography>
						<Mui.TextField
							fullWidth
							variant="outlined"
							size="small"
							inputProps={{ maxLength: 50 }}
							value={form.mobileHeaderText2 || ""}
							onChange={(e) => update({ mobileHeaderText2: e.target.value })}
							style={{ maxWidth: 576 }}
						/>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Secondary Page Header Text</Mui.Typography>
						<Mui.TextField
							fullWidth
							variant="outlined"
							size="small"
							inputProps={{ maxLength: 60 }}
							value={form.mobileSmallHeaderText || ""}
							onChange={(e) => update({ mobileSmallHeaderText: e.target.value })}
							style={{ maxWidth: 576 }}
						/>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Mobile "Call Now" Phone Number</Mui.Typography>
						<Mui.TextField
							fullWidth
							variant="outlined"
							size="small"
							inputProps={{ maxLength: 20 }}
							value={form.mobilePhoneNumber || ""}
							onChange={(e) => update({ mobilePhoneNumber: e.target.value })}
							style={{ maxWidth: 576 }}
						/>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Use Custom Header</Mui.Typography>
						<Mui.RadioGroup
							value={form.mobileUseCustomHeaderYn || "0"}
							onChange={(e) => update({ mobileUseCustomHeaderYn: e.target.value })}
							row
						>
							{CUSTOM_HEADER_OPTIONS.map((opt) => (
								<Mui.FormControlLabel
									key={opt.value}
									value={opt.value}
									control={<Mui.Radio color="secondary" />}
									label={opt.label}
								/>
							))}
						</Mui.RadioGroup>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Mobile Custom Header</Mui.Typography>
						<Mui.TextField
							fullWidth
							variant="outlined"
							size="small"
							multiline
							rows={4}
							value={form.mobileCustomHeader || ""}
							onChange={(e) => update({ mobileCustomHeader: e.target.value })}
							style={{ maxWidth: 576 }}
						/>
					</Mui.Grid>

					{form.customHomePageAllowed && (
						<>
							<Mui.Grid item>
								<Mui.Typography className={classes.fieldLabel}>Use Custom Homepage</Mui.Typography>
								<Mui.RadioGroup
									value={form.mobileUseCustomHomePageYn === "1" ? "1" : "0"}
									onChange={(e) => update({ mobileUseCustomHomePageYn: e.target.value })}
									row
								>
									<Mui.FormControlLabel value="1" control={<Mui.Radio color="secondary" />} label="On" />
									<Mui.FormControlLabel value="0" control={<Mui.Radio color="secondary" />} label="Off" />
								</Mui.RadioGroup>
							</Mui.Grid>

							<Mui.Grid item>
								<Mui.Typography className={classes.fieldLabel}>Mobile Custom Homepage URL</Mui.Typography>
								<Mui.TextField
									fullWidth
									variant="outlined"
									size="small"
									inputProps={{ maxLength: 100 }}
									value={form.mobileCustomHomePageUrl || "http://"}
									onChange={(e) => update({ mobileCustomHomePageUrl: e.target.value })}
									style={{ maxWidth: 576 }}
								/>
							</Mui.Grid>

							<Mui.Grid item>
								<Mui.Typography className={classes.fieldLabel}>
									Mobile Redirect Code
								</Mui.Typography>
								<Mui.Typography variant="body2" className={classes.sectionDescription} style={{ marginBottom: 8 }}>
									We recommend customizing the following code to redirect users on mobile devices to your mobile site:
								</Mui.Typography>
								<Mui.TextField
									fullWidth
									variant="outlined"
									size="small"
									multiline
									rows={6}
									value={redirectCode}
									InputProps={{ readOnly: true, className: classes.redirectCodeField }}
								/>
							</Mui.Grid>
						</>
					)}

					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Include Supplemental Menu Item</Mui.Typography>
						<Mui.RadioGroup
							value={form.mobileSupplementalListingsMenuYn ? "1" : "0"}
							onChange={(e) => update({ mobileSupplementalListingsMenuYn: e.target.value === "1" })}
							row
						>
							<Mui.FormControlLabel value="1" control={<Mui.Radio color="secondary" />} label="On" />
							<Mui.FormControlLabel value="0" control={<Mui.Radio color="secondary" />} label="Off" />
						</Mui.RadioGroup>
					</Mui.Grid>

					<Mui.Grid item>
						<Mui.Typography className={classes.fieldLabel}>Supplemental Menu Item Text</Mui.Typography>
						<Mui.TextField
							fullWidth
							variant="outlined"
							size="small"
							inputProps={{ maxLength: 20 }}
							value={form.mobileSupplementalListingsMenuText || ""}
							onChange={(e) => update({ mobileSupplementalListingsMenuText: e.target.value })}
							style={{ maxWidth: 576 }}
						/>
					</Mui.Grid>
				</Mui.Grid>
			</FullPageCard>

			{form.newVersionLive && (
				<Mui.Box mt={2}>
					<FullPageCard>
						<Mui.Typography className={classes.sectionHeader} style={{ marginBottom: 16 }}>Mobile Links</Mui.Typography>
						<Mui.Grid container direction="column" spacing={2}>
							{[
								{ label: "Homepage", path: `/m/${user.clientId}` },
								{ label: "Search", path: `/m/listing/search/${user.clientId}` },
								{ label: "Search By Address", path: `/m/listing/searchByAddress/${user.clientId}` },
								{ label: "Search By MLS #", path: `/m/listing/searchByListingId/${user.clientId}` },
								{ label: "Featured Listings", path: `/m/listing/featured/${user.clientId}` },
								{ label: "Supplemental Listings", path: `/m/listing/sl/${user.clientId}` },
								...(form.hasOpenHomeInfo ? [{ label: "Open Homes Search", path: `/m/listing/openHomes/${user.clientId}` }] : []),
								{ label: "Contact", path: `/m/contact/${user.clientId}` },
								...(form.agentOfficeEnabled ? [{ label: "Offices", path: `/m/team/offices/${user.clientId}` }] : []),
							].map((link) => {
								const href = `http://${extractSubdomain(user.baseUrl)}.idxre.com${link.path}`;
								return (
									<Mui.Grid item key={link.label}>
										<Mui.Typography variant="body2" style={{ fontWeight: "bold" }}>{link.label}:</Mui.Typography>
										<Mui.Link href={href} target="_blank" rel="noopener noreferrer">{href}</Mui.Link>
									</Mui.Grid>
								);
							})}
						</Mui.Grid>
					</FullPageCard>
				</Mui.Box>
			)}

			<SettingsStickyFooter
				dirty={dirty}
				saving={saving}
				onSave={handleSave}
				onCancel={handleCancel}
			/>

			<Mui.Snackbar
				open={snackbarOpen}
				autoHideDuration={4000}
				onClose={() => setSnackbarOpen(false)}
			>
				<MuiAlert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
					{snackbarMessage}
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const NativePage = withStyles(NativePageBase, styles);

const SettingsMobilePageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <NativePage {...props} />;
};

export const SettingsMobilePage = withStyles(SettingsMobilePageBase, styles);
